import { Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useGlobalLoading } from "../../utils/ContextHooks";
import { DesignState, Palette } from "../../types";
import { useEffect, useState } from "react";
import TemplateService from "../../service/template.service";
import { Box, Button } from "../../common/components";
import { createStyles, palette, spacing } from "../../styles/theme";
import { STUDIO_TOOLBAR_HEIGHT } from "../../components/StudioToolbar/StudioToolbarSkeleton";
import DesignCard, {
  DESIGN_CARD_DESKTOP_MIN_WIDTH,
  DESIGN_CARD_MOBILE_MIN_WIDTH,
} from "../../components/Design/DesignCard/DesignCard";
import AddToProjectModal from "../../components/AddToProjectModal";
import { sortDesigns } from "../../utils/SortingUtils";
import { ColorToImage } from "../../common/components/Select/ColorSelect/ColorSelect";

const styles = createStyles({
  container: {
    display: "flex",
    gap: "32px",
  },
  listContainer: {
    pt: "16px",
    maxWidth: { xs: "100%", lg: "200px" },
    mx: "auto",
    gap: "8px",
    borderLeft: {
      xs: "none",
      lg: `1px solid ${palette.grey.lightGrey}`,
    },
    display: "flex",
    flexDirection: { xs: "row", lg: "column" },
    overflowY: { xs: "hidden", lg: "auto" },
    overflowX: { xs: "auto", lg: "hidden" },
    maxHeight: {
      xs: "auto",
      lg: `calc(100vh - ${STUDIO_TOOLBAR_HEIGHT} - 16px)`,
    },
    pb: "16px",
    px: "24px",
  },
  selectedPaletteContainer: {
    boxShadow: `0 0 0 2px ${palette.brand.secondary} !important`,
    borderRadius: "8px",
    "& >div": {
      border: `1px solid ${palette.white}`,
    },
  },
  paletteContainer: {
    cursor: "pointer",
    borderRadius: "8px",
    height: { xs: "fit-content", lg: "auto" },
    "&:hover": {
      boxShadow: `0 0 0 1px ${palette.grey.primary}`,
      "& >div": {
        border: `1px solid ${palette.white}`,
      },
    },
  },
  colorsContainer: {
    display: "flex",
    borderRadius: "8px",
    border: "1px solid rgba(18, 33, 59, 0.30)",
    overflow: "hidden",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
  },
  colorBox: {
    width: { xs: "40px", lg: "64%" },
    height: "32px",
    flex: 1,
  },
  designListContainer: {
    display: "grid",
    gridTemplateColumns: {
      xs: `repeat(auto-fill,minmax(${DESIGN_CARD_MOBILE_MIN_WIDTH}, 1fr))`,
      lg: `repeat(auto-fill,minmax(${DESIGN_CARD_DESKTOP_MIN_WIDTH}, 1fr))`,
    },
    gap: {
      xs: `${spacing.regularMobile} ${spacing.groupingMobile}`,
      lg: `${spacing.regularDesktop} ${spacing.groupingDesktop}`,
    },
    pb: "16px",
    // pr: { xs: "0px", lg: "48px" },
  },
  designContainer: {
    px: { xs: spacing.wrapperMobile, lg: spacing.largeDesktop },
    pt: { xs: "24px", lg: "32px" },
    overflowY: "auto",
    maxHeight: `calc(100vh - ${STUDIO_TOOLBAR_HEIGHT})`,
    mb: { xs: "32px", lg: "48px" },
  },
});

const Templates = () => {
  const { isGloballyLoading } = useGlobalLoading();
  const { t } = useTranslation();
  const [designTemplates, setDesignTemplates] = useState<DesignState[]>([]);
  const [selectedPaletteId, setSelectedPaletteId] = useState<
    number | undefined
  >(undefined);
  const [palettes, setPalettes] = useState<Palette[]>([]);
  const [isTemplateDesignsLoading, setIsTemplateDesignsLoading] =
    useState(false);
  const [isPalettesLoading, setIsPalettesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    const fetchDesignTemplates = async () => {
      if (!selectedPaletteId) return;
      setIsTemplateDesignsLoading(true);
      const designs = await TemplateService.getTemplates(selectedPaletteId);
      setDesignTemplates(designs.data.data);
      setIsTemplateDesignsLoading(false);
    };
    if (selectedPaletteId) {
      fetchDesignTemplates();
    }
  }, [selectedPaletteId]);

  useEffect(() => {
    const fetchPalettes = async () => {
      setIsPalettesLoading(true);
      const palettes = await TemplateService.getPalettes();
      setPalettes(palettes.data.data);
      setIsPalettesLoading(false);
      const data = palettes.data?.data ?? [];
      setSelectedPaletteId(data.find((p) => p.default)?.id ?? data[0].id);
    };
    fetchPalettes();
  }, []);

  return (
    <Box
      fullWidth
      display="flex"
      flexDirection={{ xs: "column-reverse", lg: "row" }}
    >
      <Box flex={1} sx={styles.designContainer}>
        <Typography
          data-test="templates.title"
          variant="h1"
          mb={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
        >
          {isGloballyLoading ? (
            <Skeleton variant="text" width={200} />
          ) : (
            t("templates.title")
          )}
        </Typography>
        <Box fullWidth sx={styles.designListContainer}>
          {isTemplateDesignsLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <Box key={`skeleton-design-${index}`}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: {
                        xs: DESIGN_CARD_MOBILE_MIN_WIDTH,
                        lg: DESIGN_CARD_DESKTOP_MIN_WIDTH,
                      },
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={120}
                      sx={{ borderRadius: "12px", mb: 1 }}
                    />
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="60%" height={16} />
                  </Box>
                </Box>
              ))
            : designTemplates.sort(sortDesigns).map((design, index) => (
                <DesignCard
                  key={`${design.id}-${index}`}
                  design={design}
                  hideProjectName
                  hideLocked
                  hideFavorite
                  hideProductLine
                  noNavigate
                  hideTimeSince
                  hideActions
                  contentOnHover={
                    <Box>
                      <Button
                        size="small"
                        onClick={() => setSelectedTemplateId(design.id)}
                      >
                        {t("templates.add_to_project")}
                      </Button>
                    </Box>
                  }
                  imgContainerStyles={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  maxWidth={{
                    xs: "190px",
                    md: "190px",
                  }}
                />
              ))}
        </Box>
      </Box>

      <Box fullWidth sx={styles.listContainer}>
        <Typography
          variant="textRegularBold"
          display={{ xs: "none", lg: "block" }}
        >
          {t("templates.color_palette")}
        </Typography>
        {isPalettesLoading
          ? Array.from({ length: 5 }).map((_, index) => (
              <Box
                key={`skeleton-palette-${index}`}
                fullWidth
                sx={styles.paletteContainer}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  fullWidth
                  sx={styles.colorsContainer}
                >
                  <Skeleton
                    variant="rectangular"
                    sx={[styles.colorBox, { borderRadius: 0 }]}
                  />
                  <Skeleton
                    variant="rectangular"
                    sx={[styles.colorBox, { borderRadius: 0 }]}
                  />
                  <Skeleton
                    variant="rectangular"
                    sx={[styles.colorBox, { borderRadius: 0 }]}
                  />
                </Box>
              </Box>
            ))
          : palettes.map((palette, index) => {
              const isActive = palette.id === selectedPaletteId;
              const textureMainImage = ColorToImage[palette.colors.main.name];
              const textureSecondaryImage =
                ColorToImage[palette.colors.secondary.name];
              const textureAccentImage =
                ColorToImage[palette.colors.accent.name];

              return (
                <Box
                  fullWidth
                  sx={[
                    isActive ? styles.selectedPaletteContainer : null,
                    styles.paletteContainer,
                  ]}
                  onClick={() => setSelectedPaletteId(palette.id)}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    fullWidth
                    key={palette.id}
                    sx={styles.colorsContainer}
                  >
                    <Box
                      sx={[
                        styles.colorBox,
                        {
                          background: textureMainImage
                            ? "white"
                            : palette.colors.main.hex,
                        },
                      ]}
                    >
                      {textureMainImage && (
                        <img src={textureMainImage} alt="texture" />
                      )}
                    </Box>
                    <Box
                      sx={[
                        styles.colorBox,
                        {
                          background: textureSecondaryImage
                            ? "white"
                            : palette.colors.secondary.hex,
                        },
                      ]}
                    >
                      {textureSecondaryImage && (
                        <img src={textureSecondaryImage} alt="texture" />
                      )}
                    </Box>
                    <Box
                      sx={[
                        styles.colorBox,
                        {
                          background: textureAccentImage
                            ? "white"
                            : palette.colors.accent.hex,
                        },
                      ]}
                    >
                      {textureAccentImage && (
                        <img src={textureAccentImage} alt="texture" />
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
      </Box>
      <AddToProjectModal
        open={!!selectedTemplateId}
        onClose={() => setSelectedTemplateId(undefined)}
        designId={selectedTemplateId ?? ""}
      />
    </Box>
  );
};

export default Templates;
